import { ListContextProvider, toggleListItemExpand, useListContext, useListController, useRecordContext } from "ra-core"
import { OdResourcePageProps } from "~/types/resource";
import { FC, useCallback } from 'react';
import { Link as RouteLink } from 'react-router-dom';
import { useDispatch } from "react-redux";
import { Grid, Typography, Box, Card, Link, Tooltip, Divider } from "@mui/material";
import { Content } from "~/components/content/Content"
import Scrollbar from "~/components/Scrollbar";
import { Datagrid } from "~/components/list/datagrid";
import TextField from "~/components/field/TextField";
import { Pagination as DefaultPagination } from '~/components/list/pagination';
import { FunctionField } from "~/components/field";
import SizeTextField from "~/components/field/SizeTextField";
import { RecPatch } from "~/dataProvider/tools";
import { FileAuditResponseDto, FileViewResponseDto } from "~/gen/api/securefile";
import { FileCataAmbientDataContextProvider } from "~/components/securefiles/FileCataAmbientDataProvider";
import { SecureFilesTableSearchProvider } from "~/components/securefiles/SecureFilesTableSearchProvider";
import SecureFilesTableSearchRegion from "~/components/securefiles/SecureFilesTableSearchRegion";
import { genUrlWithHashid } from "~/utils/hashid";
import { useUserTimeFormat } from "~/utils/userTimeFormat";


const FileAccessLogs: FC<OdResourcePageProps> = (props) => {

  const listCtrl = useListController({ basePath: '', filter: {} })
  
  return (
    <ListContextProvider value={listCtrl}>
      <FileAuditBody {...props} />
    </ListContextProvider>
  )
}

export const FileAuditBody: FC<OdResourcePageProps> = ({
  options, resource,
}) => {
  const listCtx = useListContext()
  const format = useUserTimeFormat()
  const dispatch = useDispatch();
  const isRowExpandable = useCallback((x:FileViewResponseDto&RecPatch) => {
    return Boolean(x.error)
  }, [])
    
  return (
    <Content pageName="File Access Log" resourceName="File Access Log" breadcrumbs={options?.breadcrumbs}>
      <Grid
        container
        spacing={4}
        sx={{ mt: 2 }}
      >
        <Grid item xs={12}>
          <Card>
            <SecureFilesTableSearchProvider>
              <FileCataAmbientDataContextProvider>
                <SecureFilesTableSearchRegion />
              </FileCataAmbientDataContextProvider>
            </SecureFilesTableSearchProvider>
            <Divider />
            <Scrollbar>
              <Box sx={{ minWidth: 1150 }}>
                <Datagrid
                  hasBulkActions={false} 
                  expand={<RowExpandFileAuditExtra />}
                  isRowExpandable={isRowExpandable}
                  {...listCtx}
                  sx={{ 
                    '& tr td:nth-of-type(2)': { width: 240 },
                    '& span.filename': { 
                      maxWidth: 400, display: 'inline-block',
                      '& a': { wordWrap: 'break-word', },
                    },
                  }}
                >
                  <FunctionField source="timeStamp" label="File Access Time" showTime 
                    render={(d?:FileViewResponseDto&RecPatch) => {
                      if (!d) { return null }
                      return (
                        <Box>
                          <Typography variant="body2">{format(d.timeStamp!, 'Pp')}</Typography>
                          {d.error && 
                            <Link color="error" sx={{ cursor: 'pointer' }}
                              onClick={() => dispatch(toggleListItemExpand(resource!, d.id))}
                            >with error</Link>}
                        </Box>
                      )
                    }}
                  />
                  <FunctionField label="Name" source="fileName" sortable={false}
                    render={(d?:FileViewResponseDto&RecPatch) => (
                      <Typography component="span" variant="body2" className="filename">
                        <Tooltip title={d?.path ?? ""}>
                          <Link underline="hover"
                            component={RouteLink}
                            to={genUrlWithHashid('/secure-files/$0/show', d?.fileId ?? 0, d?.fileName)}
                          >
                            {d?.path && getFileName(d.path)}
                          </Link>
                        </Tooltip>
                      </Typography>
                    )}
                  />
                  <SizeTextField label="Size" source="size" numericSource="size" emptyText="N/A" sortable={false} />
                  <TextField label="User" source="userName" sortable={false} />
                  <TextField label="Computer" source="machine" />
                </Datagrid>
              </Box>
            </Scrollbar>
            {//@ts-ignore
              <DefaultPagination {...listCtx} />}
          </Card>
        </Grid>
      </Grid>
    </Content>
  )
}


export default FileAccessLogs;

const rgx = /^.*[\\/]/
const getFileName = (pathStr: string) => pathStr.replace(rgx, '')

const RowExpandFileAuditExtra : FC = () => {
  const d = useRecordContext<FileAuditResponseDto&RecPatch>()
  return (
    <Box role="dialog" sx={{
      mx: -2, my: -0.75,
    }}>
      <Box sx={{ bgcolor: theme => theme.palette.grey[800], px: 9, py: 1, }}>
        <Typography color="#d78484" variant="body2">{d.error}</Typography>
      </Box>
    </Box>
  )
}
